import datapoints from './datapoints';
import undoStore, { UndoStore } from '../store/undoStore';
import tombstoneOperations from 'owa-mail-list-tombstone';
import { ActionSource } from 'owa-mail-store';
import ApplyConversationActionResponseMessage from 'owa-service/lib/contract/ApplyConversationActionResponseMessage';
import ApplyMessageActionResponseMessage from 'owa-service/lib/contract/ApplyMessageActionResponseMessage';
import MoveItemResponse from 'owa-service/lib/contract/MoveItemResponse';
import { action, select } from 'satcheljs/lib/legacy';
import { createLegacyActionWithDatapoint } from 'owa-analytics-actions';

export interface UndoStoreState {
    store: UndoStore;
}

declare type UndoResponseType =
    | ApplyConversationActionResponseMessage
    | ApplyMessageActionResponseMessage
    | MoveItemResponse
    | void;

/**
 * Only supporting a "stack" of one (last undoable action)
 */
let lastUndoableAction: () => Promise<UndoResponseType>;

/**
 * Initialize the undoStack
 */
export let addActionToUndoStack = action('addActionToUndoStack')(
    select<UndoStoreState>({
        store: () => undoStore,
    })(function addActionToUndoStack(
        undoableAction: () => Promise<UndoResponseType>,
        actionFolderId: string,
        state?: UndoStoreState
    ) {
        if (undoableAction !== null) {
            lastUndoableAction = undoableAction;
            state.store.hasUndoableAction = true;
            state.store.undoableActionFolderId = actionFolderId;
        }
    })
);

/**
 * Gets last undoable action
 * @return Last undoable action
 */
export let getLastUndoableAction = function getLastUndoableAction(): () => Promise<
    UndoResponseType
> {
    return lastUndoableAction;
};

/**
 * Clears last undoable action (to null)
 */
export let clearLastUndoableAction = action('clearLastUndoableAction')(
    select<UndoStoreState>({
        store: () => undoStore,
    })(function clearLastUndoableAction(state?: UndoStoreState): void {
        lastUndoableAction = null;
        state.store.hasUndoableAction = false;
        state.store.undoableActionFolderId = null;
    })
);

/**
 * Returns if last undoable action is empty
 * @return If last undoable action is empty
 */
export let hasUndoableAction = select<UndoStoreState>({
    store: () => undoStore,
})(function hasUndoableAction(state?: UndoStoreState): boolean {
    return state.store.hasUndoableAction;
});

/**
 * Undos the last undoable action (if it exists).
 */
export let undo = createLegacyActionWithDatapoint('undo', datapoints.undo)(function undo(
    actionSource: ActionSource
): void {
    // Need to remove everything for the folder where the undo action is being taken from tombstone because if the action is being performed on a row that is
    // in tombstone then we will not honor the row notification when the action gets undone so row won't come back.
    if (undoStore.undoableActionFolderId) {
        tombstoneOperations.clearMapForFolder(undoStore.undoableActionFolderId);
    } else {
        // There are cases when we do not have folder id e.g deleteItems which can be performed from various places like RP, fileshub etc.
        // Once (VSO: 45716 - [Refactor] Remove usage of geSelectedTableView from delete item) is fixed we shall pass correct folder id to clear
        tombstoneOperations.removeAll();
    }

    const lastUndoableAction = getLastUndoableAction();

    // lastUndoableAction can be null if lazy loading of undo doesn't finish before user performs another action
    // that clears the undo stack.
    if (lastUndoableAction !== null) {
        // Perform undo
        lastUndoableAction();

        // Must clear stack so user can't try to undo same action again. Also clears UI.
        clearLastUndoableAction();
    }
});
